import { Box, Button, Dialog, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import {
	companyCommunicationAddressDetailColumns,
	companyRegistredAddressDetailColumns,
} from "data/supplierPageData";
import { useState } from "react";
import { stateList } from "data/stateMasterList";
import SyncIcon from "@mui/icons-material/Sync";
import {
	SupplierModalActions,
	SupplierModalHeader,
	modalCancelBtnSx,
	modalContentSx,
	modalFormStack,
	supplierModalPaperProps,
	supplierUi,
} from "../../../shared/supplierUi";

const EditAddressDetails = ({ addressData, setAddressData, modalOpen, setModalOpen, submitFunction }) => {
	const [updatedDetails, setUpdatedDetails] = useState(addressData);
	const [officePincodeError, setOfficePinCodeError] = useState("");

	const handlePinCodeChange = (event, col) => {
		const value = event.target.value;
		let obj = {};
		obj[col] = value;
		setUpdatedDetails({ ...updatedDetails, ...obj });

		const isValidPinCode = /^\d{6}$/.test(value);
		if (!isValidPinCode) {
			setOfficePinCodeError("pincode must be 6 digits");
		} else {
			setOfficePinCodeError("");
		}
	};

	const handleCloseModal = () => {
		setUpdatedDetails(addressData);
		setModalOpen(false);
	};
	const addressColumns = { ...companyRegistredAddressDetailColumns, ...companyCommunicationAddressDetailColumns };

	const handleCopyClick = () => {
		let obj = updatedDetails;
		obj[companyCommunicationAddressDetailColumns.communication_office_address.key] =
			updatedDetails[companyRegistredAddressDetailColumns.registred_office_address.key];
		obj[companyCommunicationAddressDetailColumns.communication_office_city.key] =
			updatedDetails[companyRegistredAddressDetailColumns.registred_office_city.key];
		obj[companyCommunicationAddressDetailColumns.communication_office_pin_code.key] =
			updatedDetails[companyRegistredAddressDetailColumns.registred_office_pin_code.key];
		obj[companyCommunicationAddressDetailColumns.communication_office_state.key] =
			updatedDetails[companyRegistredAddressDetailColumns.registred_office_state.key];
		setUpdatedDetails({ ...obj });
	};
	const handleFormSubmit = (e) => {
		e.preventDefault();
		let obj = {};
		Object.keys(addressColumns).forEach((col) => (obj[col] = updatedDetails[col]));
		if (typeof submitFunction === "function") submitFunction(obj, setModalOpen);
	};

	const sectionTitleSx = {
		fontWeight: 600,
		fontSize: 14,
		color: supplierUi.textPrimary,
		width: "100%",
		mb: 0.5,
	};

	return (
		<Dialog
			fullWidth
			maxWidth="md"
			open={modalOpen}
			onClose={handleCloseModal}
			component="form"
			onSubmit={handleFormSubmit}
			PaperProps={supplierModalPaperProps}>
			<SupplierModalHeader title="Edit address details" subtitle="Registered and communication office addresses" />
			<Box sx={{ ...modalContentSx, pt: 2.5 }}>
				<Stack width="100%" spacing={2.5}>
					<Stack direction="row" width="100%" justifyContent="space-between" flexWrap="wrap" rowGap={2} columnGap={1}>
						<Typography sx={sectionTitleSx}>Registered Address</Typography>
						<Stack sx={{ width: "100%" }} spacing={0}>
							<InputLabel>Office Address</InputLabel>
							<TextField
								size="small"
								type="text"
								multiline
								rows={2}
								value={updatedDetails.registred_office_address}
								onChange={(e) =>
									setUpdatedDetails({ ...updatedDetails, registred_office_address: e.target.value })
								}
								placeholder="Enter Address"
								required
							/>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Office City</InputLabel>
							<TextField
								size="small"
								type="text"
								value={updatedDetails.registred_office_city}
								onChange={(e) =>
									setUpdatedDetails({ ...updatedDetails, registred_office_city: e.target.value })
								}
								placeholder="Enter city name"
								required
							/>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Office State</InputLabel>
							<Select
								value={updatedDetails.registred_office_state}
								onChange={(e) =>
									setUpdatedDetails({ ...updatedDetails, registred_office_state: e.target.value })
								}
								MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
								size="small"
								fullWidth
								required>
								<MenuItem value={"select"}>Select State</MenuItem>
								{stateList &&
									stateList.map((row) => (
										<MenuItem key={row.state} value={row.state}>
											{row.state}
										</MenuItem>
									))}
							</Select>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Office Pincode</InputLabel>
							<TextField
								size="small"
								type="text"
								value={updatedDetails.registred_office_pin_code}
								onChange={(e) => handlePinCodeChange(e, "registred_office_pin_code")}
								placeholder="Enter 6 digit pincode"
								required
							/>
							{officePincodeError ? (
								<Typography variant="caption" color="error" mt={0.5}>
									{officePincodeError}
								</Typography>
							) : null}
						</Stack>
					</Stack>

					<Box>
						<Button
							startIcon={<SyncIcon />}
							onClick={handleCopyClick}
							variant="outlined"
							sx={{ ...modalCancelBtnSx, bgcolor: "#fff" }}>
							Copy to communication address
						</Button>
					</Box>

					<Stack direction="row" width="100%" justifyContent="space-between" flexWrap="wrap" rowGap={2} columnGap={1}>
						<Typography sx={sectionTitleSx}>Communication Address</Typography>
						<Stack sx={{ width: "100%" }} spacing={0}>
							<InputLabel>Office Address</InputLabel>
							<TextField
								size="small"
								type="text"
								multiline
								rows={2}
								value={updatedDetails.communication_office_address}
								onChange={(e) =>
									setUpdatedDetails({
										...updatedDetails,
										communication_office_address: e.target.value,
									})
								}
								placeholder="Enter Address"
								required
							/>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Office City</InputLabel>
							<TextField
								size="small"
								type="text"
								value={updatedDetails.communication_office_city}
								onChange={(e) =>
									setUpdatedDetails({ ...updatedDetails, communication_office_city: e.target.value })
								}
								placeholder="Enter city name"
								required
							/>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Office State</InputLabel>
							<Select
								value={updatedDetails.communication_office_state}
								onChange={(e) =>
									setUpdatedDetails({ ...updatedDetails, communication_office_state: e.target.value })
								}
								MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
								size="small"
								fullWidth
								required>
								<MenuItem value={"select"}>Select State</MenuItem>
								{stateList &&
									stateList.map((row) => (
										<MenuItem key={row.state} value={row.state}>
											{row.state}
										</MenuItem>
									))}
							</Select>
						</Stack>
						<Stack sx={modalFormStack} spacing={0}>
							<InputLabel>Office Pincode</InputLabel>
							<TextField
								size="small"
								type="text"
								value={updatedDetails.communication_office_pin_code}
								onChange={(e) => handlePinCodeChange(e, "communication_office_pin_code")}
								placeholder="Enter 6 digit pincode"
								required
							/>
						</Stack>
					</Stack>
				</Stack>
			</Box>
			<SupplierModalActions onCancel={handleCloseModal} />
		</Dialog>
	);
};

export default EditAddressDetails;
